import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Wallet, Clock, Plus, Trash2, Repeat, Coins } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { fmtMoney } from '../lib/format';
import { stickyColorFor, clientColorFor, STICKY_COLORS } from '../lib/stickyColors';
import EmojiPicker from '../components/EmojiPicker';

const todayISO = () => new Date().toISOString().slice(0, 10);

// deterministic pastel palette pick per user for a nice on-brand bubble
const userPaletteFor = (user_id) => {
  const src = String(user_id || '');
  let h = 0;
  for (let i = 0; i < src.length; i++) h = (h * 31 + src.charCodeAt(i)) >>> 0;
  return stickyColorFor(STICKY_COLORS[h % STICKY_COLORS.length].id);
};

// LVE Social sits on brand pink
const LVE_PALETTE = { bg: '#fde7ef', border: '#f4c1d5', ink: '#7a2946', tape: '#d394ab' };

const parseDate = (iso) => {
  if (!iso) return null;
  // ISO date string 'YYYY-MM-DD' - parse manually to avoid TZ drift
  const [y, m, d] = String(iso).split('-').map(Number);
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d);
  return isNaN(dt.getTime()) ? null : dt;
};
const DOW = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const MON = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// -----------------------------------------------------------------------------
// Hours tab — every user has a private hours log; managers see everyone.
// -----------------------------------------------------------------------------
const HoursTab = () => {
  const { user } = useAuth();
  const isManager = ['manager', 'talent_manager'].includes(user?.role);
  const [entries, setEntries] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [{ data: h }, { data: dir }, { data: cli }] = await Promise.all([
        api.get('/hours'),
        api.get('/team/directory'),
        api.get('/clients'),
      ]);
      setEntries(h || []); setDirectory(dir || []); setClients(cli || []);
    } catch { toast.error('failed to load hours'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm('delete this entry?')) return;
    try { await api.delete(`/hours/${id}`); setEntries((prev) => prev.filter((e) => e.id !== id)); }
    catch { toast.error('failed to delete'); }
  };

  const patch = async (id, updates) => {
    try {
      const { data } = await api.patch(`/hours/${id}`, updates);
      setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, ...data } : e)));
    } catch { toast.error('save failed'); }
  };

  const clientMap = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);
  const userMap = useMemo(() => Object.fromEntries(directory.map((u) => [u.user_id, u])), [directory]);

  // build stats + groups. Managers see every hours-enabled user; talents just see themselves
  const grouped = useMemo(() => {
    const g = {};
    // Seed one bucket per hours-enabled user so empty summaries still render
    const eligible = isManager ? directory.filter((u) => u.can_log_hours) : directory.filter((u) => u.user_id === user?.user_id && u.can_log_hours);
    eligible.forEach((u) => { g[u.user_id] = { user_id: u.user_id, entries: [] }; });
    entries.forEach((e) => {
      const bucket = g[e.user_id] || (g[e.user_id] = { user_id: e.user_id, entries: [] });
      bucket.entries.push(e);
    });
    return Object.values(g).map((bucket) => {
      const rate = Number(userMap[bucket.user_id]?.hourly_rate) || 0;
      const sortedEntries = [...bucket.entries].sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
      return { ...bucket, rate, sortedEntries };
    }).sort((a, b) => (userMap[a.user_id]?.name || '').localeCompare(userMap[b.user_id]?.name || ''));
  }, [entries, directory, userMap, isManager, user]);

  return (
    <div className="space-y-4">
      {loading ? (
        <div className="py-8 text-center text-sm text-slate-400">loading…</div>
      ) : grouped.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-12 text-center text-sm text-slate-400">no hours logged yet</div>
      ) : grouped.map((g) => (
        <UserHoursSection
          key={g.user_id}
          group={g}
          user={userMap[g.user_id]}
          clients={clients}
          clientMap={clientMap}
          isManager={isManager}
          currentUserId={user?.user_id}
          onCreated={(row) => setEntries((prev) => [row, ...prev])}
          onRemove={remove}
          onPatch={patch}
        />
      ))}
    </div>
  );
};

// -----------------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------------
const Expenses = () => {
  const { user } = useAuth();
  const canSeeCompanyExpenses = user?.role === 'manager';
  const [tab, setTab] = useState('hours');

  const tabs = [
    { id: 'hours', label: 'hours', icon: Clock },
    ...(canSeeCompanyExpenses ? [
      { id: 'recurring', label: 'monthly recurring', icon: Repeat },
      { id: 'adhoc', label: 'ad hoc', icon: Coins },
    ] : []),
  ];

  return (
    <div className="p-6 lg:p-8">
      <header className="mb-6">
        <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight flex items-center gap-2">
          <Wallet className="w-6 h-6 text-pink-500" /> {canSeeCompanyExpenses ? 'expenses' : 'hours'}
        </h1>
        <p className="text-sm text-slate-500 mt-1">{canSeeCompanyExpenses ? 'track billable hours and other outgoings before they land in the finances view.' : 'log the hours you\'ve worked so managers can see who\'s doing what.'}</p>
      </header>

      {canSeeCompanyExpenses && (
        <div className="inline-flex items-center bg-slate-100 rounded-full p-0.5 mb-5">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-3 h-9 text-xs font-semibold rounded-full inline-flex items-center gap-1.5 transition ${active ? 'bg-white text-pink-600 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                data-testid={`expenses-tab-${t.id}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>
      )}

      {tab === 'hours' && <HoursTab />}
      {tab === 'recurring' && canSeeCompanyExpenses && <ExpensesTab kind="recurring" />}
      {tab === 'adhoc' && canSeeCompanyExpenses && <ExpensesTab kind="adhoc" />}
    </div>
  );
};

export default Expenses;

const LVE_SOCIAL = '__lve__';

const UserHoursSection = ({ group, user, clients, clientMap, isManager, currentUserId, onCreated, onRemove, onPatch }) => {
  const canEditForm = isManager || group.user_id === currentUserId;
  const canEditRow = canEditForm; // same rules for editing existing entries
  const [form, setForm] = useState({ date: todayISO(), hours: '', label: '', tag: '' });
  const palette = userPaletteFor(group.user_id);

  // Period view state: 'week' or 'month'. Month state carries which month (YYYY-MM).
  const currentMonth = todayISO().slice(0, 7);
  const [view, setView] = useState('week');
  const [monthSel, setMonthSel] = useState(currentMonth);

  // Build month options: every month of current year + logged months + selected month, sorted desc.
  const monthOptions = useMemo(() => {
    const set = new Set([currentMonth, monthSel]);
    const y = new Date().getFullYear();
    for (let m = 1; m <= 12; m += 1) set.add(`${y}-${String(m).padStart(2, '0')}`);
    group.entries.forEach((e) => { if (e.date) set.add(String(e.date).slice(0, 7)); });
    return Array.from(set).sort().reverse();
  }, [group.entries, currentMonth, monthSel]);

  // Week window (Mon-Sun of current week)
  const now = new Date();
  const weekStart = new Date(now); weekStart.setDate(now.getDate() - ((now.getDay() + 6) % 7)); weekStart.setHours(0, 0, 0, 0);
  const weekEnd = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 7);

  const [selY, selM] = monthSel.split('-').map(Number);
  const monthStart = new Date(selY, (selM || 1) - 1, 1);
  const monthEnd = new Date(selY, (selM || 1), 1);

  const inView = (dateStr) => {
    const d = parseDate(dateStr); if (!d) return false;
    if (view === 'week') return d >= weekStart && d < weekEnd;
    return d >= monthStart && d < monthEnd;
  };
  const filteredEntries = useMemo(
    () => group.sortedEntries.filter((e) => inView(e.date)),
    [group.sortedEntries, view, monthSel],
  );
  const total = filteredEntries.reduce((a, e) => a + (Number(e.hours) || 0), 0);

  // Independent totals so both period buttons always show their own tally
  const weekTotal = useMemo(() => group.sortedEntries.reduce((a, e) => {
    const d = parseDate(e.date); if (!d || d < weekStart || d >= weekEnd) return a;
    return a + (Number(e.hours) || 0);
  }, 0), [group.sortedEntries, weekStart.getTime(), weekEnd.getTime()]);
  const monthTotal = useMemo(() => group.sortedEntries.reduce((a, e) => {
    const d = parseDate(e.date); if (!d || d < monthStart || d >= monthEnd) return a;
    return a + (Number(e.hours) || 0);
  }, 0), [group.sortedEntries, monthStart.getTime(), monthEnd.getTime()]);
  const submit = async () => {
    if (!form.hours) { toast.error('add the hours'); return; }
    if (!form.tag) { toast.error('tag a client or LVE Social'); return; }
    try {
      const body = {
        date: form.date,
        hours: Number(form.hours),
        label: form.label,
        client_id: form.tag,
      };
      if (isManager && group.user_id !== currentUserId) body.user_id = group.user_id;
      const { data } = await api.post('/hours', body);
      onCreated(data);
      setForm({ date: todayISO(), hours: '', label: '', tag: '' });
    } catch (e) { toast.error(e?.response?.data?.detail || 'save failed'); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
      <div
        className="px-4 py-3 border-b flex items-center gap-3 flex-wrap"
        style={{ background: palette.bg, borderColor: palette.border }}
      >
        <span
          className="inline-flex items-center justify-center w-7 h-7 rounded-full text-[11px] font-bold text-white shrink-0"
          style={{ background: palette.tape }}
        >{(user?.name || '?').slice(0, 1).toUpperCase()}</span>
        <div className="text-sm font-semibold" style={{ color: palette.ink }}>{user?.name || 'someone'}</div>
        <div className="text-[11px]" style={{ color: palette.ink, opacity: 0.7 }}>· {group.entries.length} entries</div>
        {group.rate > 0 && <div className="text-[11px]" style={{ color: palette.ink, opacity: 0.7 }}>· {fmtMoney(group.rate)}/h</div>}
      </div>
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap bg-slate-50/50">
        <button
          type="button"
          onClick={() => setView('week')}
          className={`h-9 px-3 rounded-full text-[11px] font-semibold transition inline-flex items-center gap-2 ${view === 'week' ? 'bg-pink-500 text-white shadow-sm' : 'bg-white border border-slate-200 text-slate-600 hover:border-pink-300'}`}
          data-testid={`period-week-${group.user_id}`}
        >
          this week
          <span className="tabular-nums font-bold">{weekTotal.toFixed(2)}h</span>
        </button>
        <div className={`h-9 rounded-full inline-flex items-center gap-1 transition ${view === 'month' ? 'bg-pink-500 text-white shadow-sm' : 'bg-white border border-slate-200 text-slate-600 hover:border-pink-300'}`}>
          <button
            type="button"
            onClick={() => setView('month')}
            className="pl-3 text-[11px] font-semibold inline-flex items-center gap-2"
            data-testid={`period-month-${group.user_id}`}
          >
            {new Date(selY, (selM || 1) - 1, 1).toLocaleString('en-GB', { month: 'long' })}
            <span className="tabular-nums font-bold">{monthTotal.toFixed(2)}h</span>
          </button>
          <select
            value={monthSel}
            onChange={(e) => { setMonthSel(e.target.value); setView('month'); }}
            className={`h-8 pr-2 text-[11px] font-semibold bg-transparent outline-none cursor-pointer ${view === 'month' ? 'text-white' : 'text-slate-500'}`}
            data-testid={`month-select-${group.user_id}`}
          >
            {monthOptions.map((m) => {
              const [y, mm] = m.split('-').map(Number);
              return <option key={m} value={m} style={{ color: '#111' }}>{new Date(y, mm - 1, 1).toLocaleString('en-GB', { month: 'short', year: '2-digit' })}</option>;
            })}
          </select>
        </div>
        {group.rate > 0 && (
          <span className="ml-auto text-[11px] text-slate-500">billed · <span className="font-semibold text-slate-800">{fmtMoney(group.rate * total)}</span></span>
        )}
      </div>
      {canEditForm && (
        <div className="px-4 py-3 border-b border-slate-100 grid grid-cols-2 md:grid-cols-6 gap-2 items-end bg-white">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-400">date</label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-9 text-xs" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-400">hours</label>
            <Input type="number" step="0.25" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} className="h-9 text-xs" data-testid={`hours-input-${group.user_id}`} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-400">tag</label>
            <Select value={form.tag} onValueChange={(v) => setForm({ ...form, tag: v })}>
              <SelectTrigger className="h-9 text-xs" data-testid={`hours-tag-${group.user_id}`}><SelectValue placeholder="client or LVE" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={LVE_SOCIAL}>🩷 LVE Social</SelectItem>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.logo || '📦'} {c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-slate-400">label <span className="normal-case tracking-normal text-slate-300">(optional)</span></label>
            <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="what did you work on?" className="h-9 text-xs" />
          </div>
          <Button onClick={submit} className="h-9 bg-pink-500 hover:bg-pink-600 text-white text-xs" data-testid={`hours-save-${group.user_id}`}>
            <Plus className="w-3 h-3 mr-1" /> log
          </Button>
        </div>
      )}
      {filteredEntries.length === 0 ? (
        <div className="px-4 py-6 text-center text-xs text-slate-400 italic">no hours logged for this {view === 'week' ? 'week' : 'month'}</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {filteredEntries.map((e) => (
            <HoursRow
              key={e.id}
              entry={e}
              clients={clients}
              clientMap={clientMap}
              palette={palette}
              canEdit={canEditRow}
              onRemove={onRemove}
              onPatch={onPatch}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

const HoursRow = ({ entry, clients, clientMap, palette, canEdit, onRemove, onPatch }) => {
  const [editingTag, setEditingTag] = useState(false);
  const [editingLabel, setEditingLabel] = useState(false);
  const [labelDraft, setLabelDraft] = useState(entry.label || '');

  useEffect(() => { setLabelDraft(entry.label || ''); }, [entry.label]);

  const dt = parseDate(entry.date);
  const isLve = entry.client_id === LVE_SOCIAL;
  const cli = !isLve && entry.client_id ? clientMap[entry.client_id] : null;
  const tagPalette = isLve ? LVE_PALETTE : (cli ? stickyColorFor(clientColorFor(cli)) : null);

  const commitLabel = () => {
    setEditingLabel(false);
    const next = labelDraft.trim();
    if (next !== (entry.label || '')) onPatch(entry.id, { label: next });
  };

  return (
    <li className="px-4 py-2.5 flex items-center gap-2 flex-wrap hover:bg-slate-50/60 group">
      {/* date bubbles */}
      <div className="inline-flex items-center gap-1 shrink-0">
        <Bubble>{dt ? DOW[dt.getDay()] : '—'}</Bubble>
        <Bubble className="font-semibold tabular-nums">{dt ? dt.getDate() : '—'}</Bubble>
        <Bubble>{dt ? MON[dt.getMonth()] : '—'}</Bubble>
      </div>
      {/* hours bubble */}
      <span
        className="inline-flex items-center h-6 px-2.5 rounded-full text-[11px] font-semibold tabular-nums shrink-0"
        style={{ background: palette.bg, color: palette.ink, border: `1px solid ${palette.border}` }}
      >{Number(entry.hours).toFixed(2)}h</span>
      {/* tag bubble — click to edit */}
      {editingTag && canEdit ? (
        <Select
          defaultValue={entry.client_id || ''}
          open
          onOpenChange={(o) => { if (!o) setEditingTag(false); }}
          onValueChange={(v) => { setEditingTag(false); if (v !== entry.client_id) onPatch(entry.id, { client_id: v }); }}
        >
          <SelectTrigger className="h-6 text-[11px] rounded-full px-2.5 w-[160px]"><SelectValue placeholder="tag" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={LVE_SOCIAL}>🩷 LVE Social</SelectItem>
            {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.logo || '📦'} {c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      ) : tagPalette ? (
        <button
          type="button"
          onClick={() => canEdit && setEditingTag(true)}
          disabled={!canEdit}
          className={`inline-flex items-center gap-1 h-6 px-2.5 rounded-full text-[11px] font-semibold shrink-0 max-w-[180px] truncate ${canEdit ? 'hover:brightness-95 cursor-pointer' : 'cursor-default'}`}
          style={{ background: tagPalette.bg, color: tagPalette.ink, border: `1px solid ${tagPalette.border}` }}
          title={canEdit ? 'click to change tag' : (isLve ? 'LVE Social' : cli?.name)}
        >
          {isLve ? '🩷' : (cli?.logo || '📦')}
          <span className="truncate">{isLve ? 'LVE Social' : (cli?.name || 'client')}</span>
        </button>
      ) : null}
      {/* label — click to edit */}
      {editingLabel && canEdit ? (
        <Input
          autoFocus
          value={labelDraft}
          onChange={(e) => setLabelDraft(e.target.value)}
          onBlur={commitLabel}
          onKeyDown={(e) => { if (e.key === 'Enter') commitLabel(); if (e.key === 'Escape') { setLabelDraft(entry.label || ''); setEditingLabel(false); } }}
          placeholder="add a label…"
          className="h-6 text-[12px] px-2 flex-1 min-w-[120px] max-w-[280px]"
        />
      ) : (
        <button
          type="button"
          onClick={() => canEdit && setEditingLabel(true)}
          disabled={!canEdit}
          className={`text-[12px] italic truncate flex-1 min-w-0 text-left ${entry.label ? 'text-slate-600' : 'text-slate-400'} ${canEdit ? 'hover:text-slate-800 cursor-text' : 'cursor-default'}`}
          title={canEdit ? 'click to edit label' : entry.label}
        >{entry.label || (canEdit ? '+ add label' : '')}</button>
      )}
      {canEdit && (
        <button
          onClick={() => onRemove(entry.id)}
          className="ml-auto opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600 shrink-0"
          title="remove"
        ><Trash2 className="w-3.5 h-3.5" /></button>
      )}
    </li>
  );
};

const Bubble = ({ children, className = '' }) => (
  <span className={`inline-flex items-center justify-center h-6 min-w-[38px] px-2 rounded-full text-[11px] bg-slate-100 text-slate-700 border border-slate-200 ${className}`}>{children}</span>
);

const nth = (d) => {
  if (!d) return '';
  const s = ['th','st','nd','rd']; const v = d % 100;
  return d + (s[(v - 20) % 10] || s[v] || s[0]);
};


// -----------------------------------------------------------------------------
// Recurring / Ad-hoc expenses tab
// kind='recurring' -> monthly recurring; kind='adhoc' -> one-off charge
// tag is either a client id or `__lve__` for LVE Social (talent bucket).
// -----------------------------------------------------------------------------
const ExpensesTab = ({ kind }) => {
  const recurring = kind === 'recurring';
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ date: todayISO(), end_date: '', amount: '', label: '', tag: '', recurring_day: '', emoji: '' });

  useEffect(() => {
    (async () => {
      try {
        const [{ data: e }, { data: c }] = await Promise.all([
          api.get('/expenses'),
          api.get('/clients'),
        ]);
        setItems(e || []);
        setClients(c || []);
      } catch { toast.error('failed to load expenses'); }
      finally { setLoading(false); }
    })();
  }, []);

  const clientMap = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);
  const visible = useMemo(
    () => items.filter((i) => Boolean(i.recurring) === recurring)
      .sort((a, b) => String(b.date || '').localeCompare(String(a.date || ''))),
    [items, recurring],
  );

  const submit = async () => {
    if (!form.amount) { toast.error('amount is required'); return; }
    if (!form.tag) { toast.error('tag a client or LVE Social'); return; }
    const isLve = form.tag === LVE_SOCIAL;
    const body = {
      kind: isLve ? 'talent' : 'client',
      related_id: isLve ? null : form.tag,
      label: form.label,
      amount: Number(form.amount),
      date: form.date,
      end_date: recurring ? (form.end_date || null) : null,
      recurring,
      recurring_day: recurring && form.recurring_day ? Number(form.recurring_day) : null,
      emoji: form.emoji || '',
    };
    try {
      const { data } = await api.post('/expenses', body);
      setItems((prev) => [data, ...prev]);
      setForm({ date: todayISO(), end_date: '', amount: '', label: '', tag: '', recurring_day: '', emoji: '' });
    } catch (e) { toast.error(e?.response?.data?.detail || 'save failed'); }
  };

  const patch = async (id, updates) => {
    try {
      const { data } = await api.patch(`/expenses/${id}`, updates);
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...data } : i)));
    } catch { toast.error('save failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm(`remove this ${recurring ? 'recurring' : 'ad hoc'} expense?`)) return;
    try {
      await api.delete(`/expenses/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch { toast.error('failed to delete'); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-2xl p-4">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">
          {recurring ? 'add a monthly recurring cost' : 'log an ad hoc expense'}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-400">emoji</label>
            <div className="mt-1">
              <EmojiPicker
                value={form.emoji}
                onChange={(v) => setForm({ ...form, emoji: v })}
                size="sm"
                testId={`${kind}-emoji-picker`}
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-400">{recurring ? 'starts' : 'date'}</label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-9 text-xs" />
          </div>
          {recurring && (
            <>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate-400">day of month</label>
                <Input
                  type="number"
                  min="1"
                  max="31"
                  value={form.recurring_day || ''}
                  onChange={(e) => setForm({ ...form, recurring_day: e.target.value })}
                  placeholder="e.g. 1"
                  className="h-9 text-xs"
                  data-testid="recurring-day"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate-400">ends <span className="normal-case tracking-normal text-slate-300">(optional)</span></label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="h-9 text-xs" />
              </div>
            </>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-400">amount (£)</label>
            <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="h-9 text-xs" data-testid={`${kind}-amount`} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-400">tag</label>
            <Select value={form.tag} onValueChange={(v) => setForm({ ...form, tag: v })}>
              <SelectTrigger className="h-9 text-xs" data-testid={`${kind}-tag`}><SelectValue placeholder="client or LVE" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={LVE_SOCIAL}>🩷 LVE Social</SelectItem>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.logo || '📦'} {c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className={recurring ? 'col-span-2 md:col-span-1' : 'col-span-2'}>
            <label className="text-[10px] uppercase tracking-widest text-slate-400">label <span className="normal-case tracking-normal text-slate-300">(optional)</span></label>
            <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="what's this for?" className="h-9 text-xs" />
          </div>
          <Button onClick={submit} className="h-9 bg-pink-500 hover:bg-pink-600 text-white text-xs" data-testid={`${kind}-save`}>
            <Plus className="w-3 h-3 mr-1" /> add
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="py-8 text-center text-sm text-slate-400">loading…</div>
        ) : visible.length === 0 ? (
          <div className="py-10 text-center text-sm text-slate-400">no {recurring ? 'recurring' : 'ad hoc'} expenses yet</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {visible.map((it) => (
              <ExpenseRow
                key={it.id}
                item={it}
                recurring={recurring}
                clients={clients}
                clientMap={clientMap}
                onRemove={remove}
                onPatch={patch}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const ExpenseRow = ({ item, recurring, clients, clientMap, onRemove, onPatch }) => {
  const [editingTag, setEditingTag] = useState(false);
  const [editingLabel, setEditingLabel] = useState(false);
  const [labelDraft, setLabelDraft] = useState(item.label || '');

  useEffect(() => { setLabelDraft(item.label || ''); }, [item.label]);

  const isLve = item.kind === 'talent';
  const cli = !isLve && item.related_id ? clientMap[item.related_id] : null;
  const tagPalette = isLve ? LVE_PALETTE : (cli ? stickyColorFor(clientColorFor(cli)) : null);

  const currentTagValue = isLve ? LVE_SOCIAL : (item.related_id || '');
  const setTag = (v) => {
    const nextLve = v === LVE_SOCIAL;
    onPatch(item.id, { kind: nextLve ? 'talent' : 'client', related_id: nextLve ? null : v });
    setEditingTag(false);
  };

  const commitLabel = () => {
    setEditingLabel(false);
    const next = labelDraft.trim();
    if (next !== (item.label || '')) onPatch(item.id, { label: next });
  };

  const dt = parseDate(item.date);
  const ed = parseDate(item.end_date);

  return (
    <li className="px-4 py-2.5 flex items-center gap-2 flex-wrap hover:bg-slate-50/60 group">
      {/* date bubbles */}
      <div className="inline-flex items-center gap-1 shrink-0">
        <Bubble>{dt ? DOW[dt.getDay()] : '—'}</Bubble>
        <Bubble className="font-semibold tabular-nums">{dt ? dt.getDate() : '—'}</Bubble>
        <Bubble>{dt ? MON[dt.getMonth()] : '—'}</Bubble>
        {recurring && (
          <span className="text-[10px] text-slate-500 ml-1">
            {item.recurring_day ? `· ${nth(item.recurring_day)} each month` : ''} → {ed ? `${ed.getDate()} ${MON[ed.getMonth()]} ${ed.getFullYear()}` : 'ongoing'}
          </span>
        )}
      </div>
      {/* amount */}
      <span className="inline-flex items-center h-6 px-2.5 rounded-full text-[11px] font-semibold tabular-nums shrink-0 bg-rose-50 text-rose-700 border border-rose-100">
        {fmtMoney(Number(item.amount) || 0)}{recurring && <span className="ml-1 font-normal opacity-70">/mo</span>}
      </span>
      {/* tag bubble */}
      {editingTag ? (
        <Select
          defaultValue={currentTagValue}
          open
          onOpenChange={(o) => { if (!o) setEditingTag(false); }}
          onValueChange={setTag}
        >
          <SelectTrigger className="h-6 text-[11px] rounded-full px-2.5 w-[160px]"><SelectValue placeholder="tag" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={LVE_SOCIAL}>🩷 LVE Social</SelectItem>
            {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.logo || '📦'} {c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      ) : (
        <button
          type="button"
          onClick={() => setEditingTag(true)}
          className="inline-flex items-center gap-1 h-6 px-2.5 rounded-full text-[11px] font-semibold shrink-0 max-w-[180px] truncate hover:brightness-95 cursor-pointer"
          style={tagPalette ? { background: tagPalette.bg, color: tagPalette.ink, border: `1px solid ${tagPalette.border}` } : { background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }}
          title="click to change tag"
        >
          {isLve ? '🩷' : (cli?.logo || '📦')}
          <span className="truncate">{isLve ? 'LVE Social' : (cli?.name || 'untagged')}</span>
        </button>
      )}
      {/* label */}
      {editingLabel ? (
        <Input
          autoFocus
          value={labelDraft}
          onChange={(e) => setLabelDraft(e.target.value)}
          onBlur={commitLabel}
          onKeyDown={(e) => { if (e.key === 'Enter') commitLabel(); if (e.key === 'Escape') { setLabelDraft(item.label || ''); setEditingLabel(false); } }}
          placeholder="add a label…"
          className="h-6 text-[12px] px-2 flex-1 min-w-[120px] max-w-[280px]"
        />
      ) : (
        <button
          type="button"
          onClick={() => setEditingLabel(true)}
          className={`text-[12px] italic truncate flex-1 min-w-0 text-left ${item.label ? 'text-slate-600' : 'text-slate-400'} hover:text-slate-800 cursor-text`}
          title="click to edit label"
        >{item.label || '+ add label'}</button>
      )}
      <button
        onClick={() => onRemove(item.id)}
        className="ml-auto opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600 shrink-0"
        title="remove"
      ><Trash2 className="w-3.5 h-3.5" /></button>
    </li>
  );
};
